import React, { useState, useEffect, useRef } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  Fade
} from '@mui/material';
import { VolumeUp as VolumeUpIcon } from '@mui/icons-material';

/**
 * Componente de pop-up para autorização de vocalização
 * Exibe um diálogo elegante solicitando permissão para iniciar a vocalização
 */
const VocalizacaoPopup = ({ onAutorizar }) => {
  const [open, setOpen] = useState(true);
  const [pulsar, setPulsar] = useState(false);
  // Referência para áudio silencioso para desbloquear navegadores
  const audioRef = useRef(null);

  // Efeito para criar animação de pulsar
  useEffect(() => {
    const interval = setInterval(() => {
      setPulsar(prev => !prev);
    }, 1500);
    
    // Criar o áudio silencioso para desbloqueio
    const silentAudio = new Audio("data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjMyLjEwNAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACAgBA//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjU0AAAAAAAAAAAAAAAAJAM=");
    silentAudio.volume = 0.01; // Volume extremamente baixo
    audioRef.current = silentAudio;
    
    return () => {
      clearInterval(interval);
      // Limpar o áudio ao desmontar
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Função para autorizar e desbloquear o áudio
  const handleAutorizar = async () => {
    try {
      // Tocar o áudio silencioso para desbloquear navegadores
      if (audioRef.current) {
        try {
          await audioRef.current.play();
          console.log('Áudio desbloqueado com sucesso');
        } catch (e) {
          console.warn('Não foi possível desbloquear o áudio:', e);
          // Continuar mesmo sem conseguir desbloquear
        }
      }
      
      // Fechar o diálogo
      setOpen(false);
      
      // Chamar callback de autorização
      if (onAutorizar) {
        onAutorizar(true);
      }
      
      console.log('Vocalização autorizada pelo usuário');
    } catch (error) {
      console.error('Erro ao autorizar vocalização:', error);
      // Fechar o diálogo mesmo em caso de erro
      setOpen(false);
      if (onAutorizar) {
        onAutorizar(true);
      }
    }
  };

  return (
    <Dialog 
      open={open} 
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          background: 'linear-gradient(145deg, #ffffff, #f5f5f5)'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pt: 4, 
        fontWeight: 'bold',
        color: 'text.primary'
      }}>
        Autorização de Áudio
      </DialogTitle>
      
      <DialogContent sx={{ textAlign: 'center', py: 3 }}>
        <Fade in={pulsar} timeout={800}>
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'center',
            mb: 3
          }}>
            <VolumeUpIcon sx={{ 
              fontSize: '5rem', 
              color: '#3498db', 
              filter: pulsar ? 'drop-shadow(0 0 8px rgba(52, 152, 219, 0.5))' : 'none',
              transition: 'all 0.5s ease'
            }} />
          </Box>
        </Fade>
        
        <Typography variant="body1" sx={{ mb: 2 }}>
          Para ativar a vocalização das senhas chamadas, precisamos da sua autorização.
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Ao clicar em "Iniciar Sistema", você permitirá a reprodução automática de áudio 
          em seu navegador quando novas senhas forem chamadas.
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
        <Button 
          variant="contained" 
          size="large"
          onClick={handleAutorizar}
          sx={{
            bgcolor: '#2980b9',
            color: 'white',
            fontWeight: 'bold',
            px: 4,
            py: 1.5,
            borderRadius: '8px',
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(41, 128, 185, 0.3)',
            '&:hover': {
              bgcolor: '#3498db',
              boxShadow: '0 6px 16px rgba(41, 128, 185, 0.4)',
            }
          }}
        >
          Iniciar Sistema
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VocalizacaoPopup; 